import { useZodQuery } from '@repo/hooks/useZodQuery';
import React from 'react';
import GET_DIDS_FOR_ACCOUNT_Q from '../gql/getDidsForAccount.gql';
import { ButtonLink } from '@repo/ui/Button';
import { Link } from 'react-router';

import { Table } from '@repo/ui/Table';
import { Form } from '@repo/ui/Form';
import { PAGE_SCHEMA, Pagination, decodePaging } from '@repo/ui/Pagination';
import { useQuery } from '@apollo/client/react';
import { coerce } from '@repo/util/coerceForTable';
import { toQueryList } from '@repo/util/toQueryList';
import { combineDateRange } from '@repo/util/combineDateRange';
import { coerceValue, NOT_NULL } from '@repo/util/coerceValueForQuery';
import { toAdvancedQueryList } from '@repo/util/toAdvancedQueryList';
import { z } from 'zod';

import AutoRespondersTableFilter from './AutoRespondersTableFilter';

import { useUser } from '@repo/hooks/useUser';
import { POLICIES } from '@/lib/Authorization';
import type { Mercury } from '@repo/types/Models';

const AUTORESPONDER_QUERY_SCHEMA = PAGE_SCHEMA.extend({
  number: z.string().default(''),
});

export interface AutoRespondersTableProps {
  accountId: string;
}

const PAGE_SIZE = 10;

const AutoRespondersTable = ({ accountId }: AutoRespondersTableProps) => {
  const user = useUser();

  const filter = useZodQuery({
    schema: AUTORESPONDER_QUERY_SCHEMA,
    ignoreList: ['page'],
    coerce,
  });
  const { page, ...queryFields } = filter.queryValues;
  const queryList = [
    ...toQueryList(
      {
        ...queryFields,
      },
      coerceValue,
      ['number']
    ),
    ...toAdvancedQueryList({ auto_response_id: NOT_NULL }),
  ];

  const { loading, error, data } = useQuery(GET_DIDS_FOR_ACCOUNT_Q, {
    variables: {
      id: accountId,
      ...decodePaging(page, PAGE_SIZE),
      queryList,
    },
    notifyOnNetworkStatusChange: true,
  });

  if (error) {
    console.error(error);
  }

  const { edges: didNodes, pageInfo } = data?.didsForAccount ?? {};

  return (
    <div>
      <Form {...filter} variant="horizontal" className={'mb-6'}>
        <AutoRespondersTableFilter />
      </Form>
      <Table
        loading={loading}
        pageSize={PAGE_SIZE}
        data={didNodes?.map((did) => did.node) ?? []}
        dataType="dids"
        error={!!error}
        columns={[
          // {
          //   title: 'Name',
          //   width: 'lg',
          //   Cell({ entry: { autoResponse, id } }) {
          //     return (
          //       <a
          //         className="underline"
          //         href={`/a/${accountId}/numbers/${id}/auto-responders`}
          //       >
          //         {autoResponse?.name
          //           ? autoResponse?.name
          //           : 'UNNAMED AUTO RESPONDER'}
          //       </a>
          //     );
          //   },
          // },
          // {
          //   title: 'Description',
          //   width: 'xl',
          //   Cell({ entry: { autoResponse } }) {
          //     return <p>{autoResponse?.notes}</p>;
          //   },
          // },

          {
            title: 'Assigned number',
            Cell({ entry: { number, id } }) {
              return POLICIES['dids:show'](user) ? (
                <Link className="underline" to={`/a/${accountId}/numbers/${id}`}>
                  {number}
                </Link>
              ) : (
                <p> {number}</p>
              );
            },
          },

          {
            title: 'General Auto Responder',
            Cell({ entry: { autoResponse } }) {
              const hasWildcard = autoResponse?.keywords?.some(
                (kw: Mercury.Models.MessagingAutoResponseKeyword) => kw.isWildcard
              );
              return <span>{hasWildcard ? 'Active' : 'Inactive'}</span>;
            },
          },

          {
            title: 'Keywords',
            Cell({ entry: { autoResponse } }) {
              const KEYWORD_DISPLAY_LIMIT = 3;
              const visibleKeywords =
                autoResponse?.keywords?.filter(
                  (kw: Mercury.Models.MessagingAutoResponseKeyword) => !kw.isWildcard
                ) ?? [];
              const shown = visibleKeywords.slice(0, KEYWORD_DISPLAY_LIMIT);
              const overflow = visibleKeywords.length - shown.length;
              return (
                <div className="truncate">
                  {shown
                    .map((kw: Mercury.Models.MessagingAutoResponseKeyword) => kw.keyword)
                    .join(', ')}
                  {overflow > 0 && (
                    <span
                      className="ml-1 text-grey-600"
                      title={visibleKeywords
                        .map((kw: Mercury.Models.MessagingAutoResponseKeyword) => kw.keyword)
                        .join(', ')}
                    >
                      +{overflow} more
                    </span>
                  )}
                </div>
              );
            },
          },

          {
            title: 'Actions',
            Cell({ entry: { id } }) {
              return (
                <ButtonLink as={Link} href={`/a/${accountId}/numbers/${id}/auto-responders`}>
                  Edit Auto Responder
                </ButtonLink>
              );
            },
          },
        ]}
      />
      {!error ? <Pagination {...{ pageInfo, filter }} /> : null}
    </div>
  );
};

export default AutoRespondersTable;
